import React from 'react';
import { Check, Minus, Monitor, Smartphone } from 'lucide-react';
import { AppSelectDropdown, type AppSelectOption } from '../../../components/AppSelectDropdown';
import { RolePresetGrid, type RolePresetOption } from './RolePresetSelect';
import type { InvitePermissionLists } from '../capabilitiesUi';
import { SETTINGS_FIELD_LABEL, SETTINGS_SELECT_TRIGGER_CLASS } from '../utils/settingsSectionStyles';

type UserAccessSettingsFieldsProps = {
  role: string;
  onRoleChange: (value: string) => void;
  roleOptions: AppSelectOption[];
  showRoleSelect?: boolean;
  roleDisabled?: boolean;
  presets: RolePresetOption[];
  presetId: string;
  onPresetChange: (presetId: string) => void;
  permissionLists: InvitePermissionLists;
  extraContent?: React.ReactNode;
};

export function UserAccessSettingsFields({
  role,
  onRoleChange,
  roleOptions,
  showRoleSelect = true,
  roleDisabled = false,
  presets,
  presetId,
  onPresetChange,
  permissionLists,
  extraContent,
}: UserAccessSettingsFieldsProps) {
  return (
    <>
      {showRoleSelect ? (
        <div>
          <label className={`mb-1.5 block ${SETTINGS_FIELD_LABEL}`}>
            Mitgliedschaft <span className="text-red-500">*</span>
          </label>
          <AppSelectDropdown
            value={role}
            onChange={onRoleChange}
            options={roleOptions}
            className="w-full"
            disabled={roleDisabled}
            triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
          />
        </div>
      ) : null}

      <div>
        <label className={`mb-2 block ${SETTINGS_FIELD_LABEL}`}>
          Rolle <span className="text-red-500">*</span>
        </label>
        <RolePresetGrid presets={presets} value={presetId} onChange={onPresetChange} />
      </div>

      {extraContent}

      <div className="rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3">
        <p className={`mb-2 ${SETTINGS_FIELD_LABEL}`}>Diese Person kann:</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className={`mb-2 inline-flex items-center gap-1.5 ${SETTINGS_FIELD_LABEL}`}>
              <Monitor className="h-3.5 w-3.5" />
              Web
            </div>
            <ul className="space-y-1.5">
              {permissionLists.web.items.map((item) =>
                item.kind === 'allow' ? (
                  <li key={`web-allow-${item.label}`} className="flex items-start gap-2 text-sm text-app-text-muted">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-pipedrive-blue" />
                    <span>{item.label}</span>
                  </li>
                ) : (
                  <li key={`web-deny-${item.label}`} className="flex items-start gap-2 text-sm text-app-text-subtle">
                    <Minus className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span>{item.label}</span>
                  </li>
                ),
              )}
              {permissionLists.web.items.length === 0 ? (
                <li className="text-sm text-slate-400">Kein Web-Zugriff.</li>
              ) : null}
            </ul>
          </div>
          <div>
            <div className={`mb-2 inline-flex items-center gap-1.5 ${SETTINGS_FIELD_LABEL}`}>
              <Smartphone className="h-3.5 w-3.5" />
              App
            </div>
            <ul className="space-y-1.5">
              {permissionLists.app.items.map((item) =>
                item.kind === 'allow' ? (
                  <li key={`app-allow-${item.label}`} className="flex items-start gap-2 text-sm text-app-text-muted">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-pipedrive-blue" />
                    <span>{item.label}</span>
                  </li>
                ) : (
                  <li key={`app-deny-${item.label}`} className="flex items-start gap-2 text-sm text-app-text-subtle">
                    <Minus className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span>{item.label}</span>
                  </li>
                ),
              )}
              {permissionLists.app.items.length === 0 ? (
                <li className="text-sm text-slate-400">Kein App-Zugriff.</li>
              ) : null}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
